<template>
  <div style="display: flex">
    <div :id="legendId" class="legend"></div>
    <div class="report-chart">
      <div v-if="isSelectedDyspnea" class="btn-group" style="margin-left: 60px;">
        <button
          type="button"
          class="btn dark-text btn-outline-none"
          :class="selectedActivityLevel === 0 ? 'btn-secondary-light' : 'btn-outline-secondary'"
          @click="setActivityLevel(0)"
        >
          {{ $tc('courseReport.atRest', 1) }}
        </button>
        <button
          type="button"
          class="btn dark-text btn-outline-none"
          :class="selectedActivityLevel === 1 ? 'btn-secondary-light' : 'btn-outline-secondary'"
          @click="setActivityLevel(1)"
        >
          {{ $tc('courseReport.withexertion', 1) }}
        </button>
      </div>
      <div
        :id="chartId"
        ref="chartReport"
        class="chartdiv w-100 float-left"
        :class="[{ 'h-100': !isSelectedDyspnea }, { 'is-dyspnea': isSelectedDyspnea }]"
      ></div>
    </div>
  </div>
</template>

<script>
import i18n from '@/i18n.js'
import { legendAndChart } from '@/mixins/legend&chart'
import declension from '@/modules/declension'
import { formatDate } from '@/mixins/format-date.js'
import { timePeriods } from '@/main.config'

const legendType = {
  // вес
  weight: 0,
  // одышка
  dyspnea: 1,
  // боль в сердце
  heartache: 2,
  // боль за грудиной
  chestpain: 3
}

const legendMap = {
  [legendType.weight]: 'body_weigth',
  [legendType.dyspnea]: 'breath_shortness_on_exertion_intensity',
  [legendType.heartache]: 'heart_pain_attacks_number',
  [legendType.chestpain]: 'pain_behind_chest_intensity'
}

const legendUnit = {
  0: ` ${i18n.t('unit.kg')}`,
  1: `/10`,
  2: ``,
  3: `/10`
}

const axisTitle = {
  0: `${i18n.t('unit.kgH')}`,
  1: `${i18n.t('unit.intensityAssessment')}`,
  2: `${i18n.t('unit.numAttacks')}`,
  3: `${i18n.t('unit.intensityAssessment')}`
}

const activityLevel = {
  atRest: 0,
  onExertion: 1
}

export default {
  name: 'DiaryGraph',
  mixins: [legendAndChart, formatDate],
  components: {},
  props: {
    diaryData: {
      type: Array,
      required: true
    },
    activeTimeData: {
      type: Number,
      required: true
    },
    selectedDate: {
      type: Date,
      required: false
    }
  },
  data() {
    return {
      chart: null,
      listOfLegend: {
        0: this.$t('courseReport.weight'),
        1: this.$t('courseReport.dyspnea'),
        2: this.$t('courseReport.heartpain'),
        3: this.$t('courseReport.chestpain')
      },
      selectedLegend: legendType.weight,
      colorList: ['#B139E5', '#4C43E5', '#E57B39', '#9AC724'],
      legendId: 'legendDiary',
      chartId: 'chartDiary',
      selectedActivityLevel: activityLevel.atRest
    }
  },
  mounted() {
    this.setAxisYTitle()
  },
  computed: {
    isSelectedDyspnea() {
      return this.selectedLegend === legendType.dyspnea
    }
  },
  watch: {
    activeTimeData() {
      this.updateChartData()
    },
    selectedDate() {
      this.updateChartData()
    }
  },
  methods: {
    getData() {
      const chartData = []
      let data = this.getDataWithTimeFilter(this.diaryData)
      let legendName = legendMap[this.selectedLegend]
      for (let i = 0; i < data.length; i++) {
        if (this.isSelectedDyspnea) {
          legendName =
            this.selectedActivityLevel === activityLevel.atRest
              ? 'breath_shortness_at_rest_intensity'
              : 'breath_shortness_on_exertion_intensity'
        }
        const dataItem = {
          date:
            this.getFormattedDate(data[i].createdAt, true) + ' ' + this.getFormattedTime(data[i].createdAt),
          value: data[i].content[legendName] ?? 0,
          shaded: true,
          shadedText: this.getShadedText(this.selectedLegend, data[i].content[legendName]),
          unit: legendUnit[this.selectedLegend]
        }
        chartData.push(dataItem)
      }

      return chartData
    },
    getDataWithTimeFilter(diaryData) {
      const currentDate = new Date()
      let timeDataStartDate
      let timeDataEndDate = currentDate
      switch (this.activeTimeData) {
        case timePeriods.allTime:
          this.label.text = ''
          return diaryData
        case timePeriods.oneDay:
          timeDataStartDate = this.selectedDate
          timeDataEndDate = new Date(
            this.selectedDate.getFullYear(),
            this.selectedDate.getMonth(),
            this.selectedDate.getDate() + 1
          )
          break
        case timePeriods.oneWeek:
          timeDataStartDate = new Date(
            this.selectedDate.getFullYear(),
            this.selectedDate.getMonth(),
            this.selectedDate.getDate() - 6
          )
          timeDataEndDate = new Date(
            this.selectedDate.getFullYear(),
            this.selectedDate.getMonth(),
            this.selectedDate.getDate() + 1
          )
          break
        case timePeriods.oneMonth:
          timeDataStartDate = new Date(
            currentDate.getFullYear(),
            currentDate.getMonth(),
            currentDate.getDate() - 31
          )
          break
        case timePeriods.threeMonths:
          timeDataStartDate = new Date(
            currentDate.getFullYear(),
            currentDate.getMonth(),
            currentDate.getDate() - 92
          )
          break
      }
      const filteredArray = diaryData.filter(item => {
        const itemDate = new Date(item.createdAt)
        return itemDate >= timeDataStartDate && itemDate <= timeDataEndDate
      })

      this.label.text =
        this.activeTimeData === timePeriods.oneDay
          ? `${this.getFormattedDate(timeDataStartDate, true)}`
          : `${this.getFormattedDate(timeDataStartDate, true)} - ${this.getFormattedDate(
              timeDataEndDate,
              true
            )}`
      return filteredArray
    },
    getShadedText(legend, value) {
      switch (legend) {
        case legendType.weight:
          return undefined
        case legendType.dyspnea:
          return this.getIntensiveName(value)
        case legendType.heartache:
          return declension(value, [
            this.$tc('courseReport.attack', 0),
            this.$tc('courseReport.attack', 1),
            this.$tc('courseReport.attack', 2)
          ])
        case legendType.chestpain:
          return this.getIntensiveName(value)
      }
    },
    getIntensiveName(score) {
      if (score >= 1 && score <= 3) {
        return this.$tc('courseReport.mild', 1)
      } else if (score >= 4 && score <= 6) {
        return this.$tc('courseReport.moderate', 1)
      } else if (score === 7 || score === 8) {
        return this.$tc('courseReport.severe', 1)
      } else if (score === 9 || score === 10) {
        return this.$tc('courseReport.extreme', 1)
      } else {
        return this.$tc('courseReport.notbothered', 1)
      }
    },
    handleLegendItemClick(index, legendItems) {
      this.selectedLegend = index
      this.chart.data = []
      this.updateChartData()

      this.series.stroke = this.chart.colors.getIndex(index).hex
      this.series.fill = this.chart.colors.getIndex(index).hex

      legendItems.forEach((legendItem, i) => {
        legendItem.classList.remove('active-item')
        if (i === index) {
          legendItem.classList.add('active-item')
        }
      })
    },
    updateChartData() {
      this.chart.data = this.getData()
      this.setAxisYTitle()
    },
    setActivityLevel(value) {
      this.selectedActivityLevel = value
      this.chart.data = []
      this.updateChartData()
    },
    setAxisYTitle() {
      this.valueAxis.title.text = axisTitle[this.selectedLegend]
      this.valueAxis.title.fontSize = '12px'
      this.valueAxis.title.fontFamily = 'Arial'
    }
  }
}
</script>

<style>
.is-dyspnea {
  height: calc(100% - 30px);
}
</style>
